import { DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik } from '@strata/react-inputs';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { putApproveDraft, putUpdateDraftStatus } from 'api/clinicalPolicyAPI';
import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { type PolicySummary } from 'store/api/clinicalPolicyApi';

interface ApprovePolicyDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
    policyData?: PolicySummary;
}

export const ApprovePolicyDialog = ({
    isOpen,
    onSecondaryButtonClick,
    policyData = undefined,
}: ApprovePolicyDialogProps) => {
    const { payerId } = useAppSelector((state) => state.auth);
    return (
        <Formik
            initialValues={{ effectiveDate: policyData?.effectiveDate, comment: '' }}
            enableReinitialize
            validationSchema={Yup.object({
                effectiveDate: DateInputFormik.validator()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(dayjs().add(1, 'day').startOf('day').toDate(), 'Must be future date'),
                comment: Yup.string(),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    if (policyData) {
                        await putApproveDraft({
                            payerId: payerId!,
                            draftId: policyData.draftId,
                            effectiveDate: formatDate(
                                values.effectiveDate,
                                DateFormats.HYPHEN_DATE,
                            ),
                            comment: values.comment,
                        });
                        await putUpdateDraftStatus({
                            payerId: payerId!,
                            draftId: policyData.draftId,
                            policyStatus: ClinicalPolicyStatusIndicator.Statuses.PEND_PROD,
                            comment: values.comment,
                        });
                        resetForm();
                        onSecondaryButtonClick();
                    }
                } catch (err) {
                    handleError('An error occurred approving the policy.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Approve Policy"
                    isOpen={isOpen}
                    primaryButtonLabel="Approve"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={(e) => {
                        formik.handleReset(e);
                        onSecondaryButtonClick();
                    }}
                    dialogContent={
                        <>
                            <p className="margin__bottom--large">
                                Approving this policy will make it live in production on the
                                specified effective date below.
                            </p>
                            <DateInputFormik
                                id="effectiveDate"
                                label="Policy Effective Date"
                                required
                                minDate={dayjs().add(1, 'day').startOf('day').toDate()}
                            />
                            <TextAreaFormik id="comment" label="Comment" />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
